/**
 * Entries View - Admin view to review attorney entries over a date range.
 *
 * Toolbar: the shared FilterMenu (application, status, matter, client and
 * timekeeper) on the left, a centered date-range picker. The shared provider
 * supplies the date and filter selection, while the v2 admin query applies the
 * resolved firm-wide scope. The Calendar lives in its own sidebar nav item.
 *
 * Uses the same EntriesTable component as the attorney view (DRY principle).
 * Differences from attorney view:
 * - No checkboxes (read-only)
 * - Click opens detail dialog instead of edit dialog
 * - No export/group/delete actions
 */

import {
	type ColumnFiltersState,
	type ColumnSizingState,
	type ExpandedState,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getGroupedRowModel,
	getSortedRowModel,
	type OnChangeFn,
	type Row,
	type RowSelectionState,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from "@tanstack/react-table";
import { Filter } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import {
	DEFAULT_COLUMN_ORDER,
	DEFAULT_COLUMN_VISIBILITY,
	type EntriesViewMeta,
	getEntriesColumns,
} from "@/features/attorney/views/Entries/columns";
import { EntriesTable } from "@/features/attorney/views/Entries/EntriesTable";
import {
	computeDateFilter,
	makeEntryPredicate,
} from "@/features/attorney/views/Entries/entryFilter";
import { entryFiltersToEntriesScope } from "@/features/attorney/views/Entries/entryQueryScope";
import { isDefaultFilters } from "@/features/attorney/views/Entries/filters/defaults";
import { FilterMenu } from "@/features/attorney/views/Entries/filters/FilterMenu";
import { useFilterOptions } from "@/features/attorney/views/Entries/filters/useFilterOptions";
import { useFilters } from "@/features/attorney/views/Entries/filters/useFilters";
import type { Entry } from "@/features/attorney/views/Entries/normalize";
import { useEntriesCache } from "@/shared/cache";
import { useEntryViewState } from "@/shared/contexts/EntryViewStateContext";
import { cn } from "@/shared/lib/utils";
import { logger } from "@/shared/utils/logger";
import { useApi } from "@/shared/utils/request";
import {
	DatePicker,
	DatePickerDefaultFooter,
} from "@/v2/components/date-picker/DatePicker";
import { Button } from "@/v2/components/ui/button";
import { formatEntryId } from "@/v2/hooks/actions/entryId";
import AdminEntryDetailDialog, {
	type FeedbackPayload,
} from "../components/AdminEntryDetailDialog";
import { useProfessionals, useRestoreAndExport } from "../hooks/useAdminData";
import { usePersistedState } from "../hooks/usePersistedState";
import { professionalToTimekeeper } from "../utils/timekeepers";
import styles from "./AdminViews.module.css";

const ADMIN_DEFAULT_STATUSES = ["tracked", "reviewed", "exported"] as const;
const EMPTY_FILTER_OPTION_ENTRIES: [] = [];

export default function EntriesView() {
	const { date, dateRange, setDate, setDateRange } = useEntryViewState();
	const selectedRange = useMemo(
		() =>
			dateRange.from && dateRange.to
				? { from: dateRange.from, to: dateRange.to }
				: { from: date, to: date },
		[date, dateRange],
	);

	// Table state for the shared Entries table.
	const [sorting, setSorting] = usePersistedState<SortingState>(
		"admin:entries:sorting",
		[{ id: "date", desc: true }],
	);
	const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
		(updater) => {
			setSorting(typeof updater === "function" ? updater(sorting) : updater);
		},
		[setSorting, sorting],
	);
	const [expanded, setExpanded] = useState<ExpandedState>(true);
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
	const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
	const columnVisibility = useMemo<VisibilityState>(
		() => ({
			...DEFAULT_COLUMN_VISIBILITY,
			_select: false,
			billableCategory: false,
		}),
		[],
	);

	// Dialog state
	const [selectedEntry, setSelectedEntry] = useState<Entry | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [isSavingFeedback, setIsSavingFeedback] = useState(false);

	// API for saving feedback and restoring deleted entries
	const { makeApiRequest } = useApi();
	const { restoreAndExport } = useRestoreAndExport();
	const [isRestoring, setIsRestoring] = useState(false);

	// All active timekeepers — used to scope the fetch, build the FilterMenu
	// timekeeper options, and resolve names in the timekeeper column.
	const {
		professionals,
		loading: profLoading,
		error: professionalsError,
		refetch: refetchProfessionals,
	} = useProfessionals({
		activeOnly: true,
	});
	const timekeepers = useMemo(
		() => professionals.map(professionalToTimekeeper),
		[professionals],
	);
	const profIds = useMemo(
		() => professionals.map((p) => p.prof_id),
		[professionals],
	);

	const fieldOptions = useFilterOptions(
		EMPTY_FILTER_OPTION_ENTRIES,
		timekeepers,
	);
	const {
		filters,
		defaultFilters,
		toggleFieldValue,
		selectAll,
		selectNone,
		reset,
	} = useFilters(fieldOptions, {
		defaultStatuses: ADMIN_DEFAULT_STATUSES,
		allowedTimekeeperIds: profLoading ? undefined : profIds,
	});
	const resolvedTimekeeperIds =
		filters.timekeeper ?? (profLoading ? [] : profIds);
	const dateFilter = useMemo(
		() => computeDateFilter(date, dateRange),
		[date, dateRange],
	);
	const entriesScope = useMemo(
		() => ({
			...entryFiltersToEntriesScope(filters, dateFilter, {
				includeAttachments: false,
			}),
			timekeeperIds: resolvedTimekeeperIds,
			adminMode: true,
		}),
		[dateFilter, filters, resolvedTimekeeperIds],
	);
	const {
		entries,
		isLoading: entriesLoading,
		error: entriesError,
		updateEntries,
		refetch: refetchEntries,
	} = useEntriesCache(entriesScope, {
		enabled: !profLoading && profIds.length > 0,
	});

	const hasActiveFilters = !isDefaultFilters(filters, defaultFilters);

	// Keep the shared table predicate as a defensive mirror of the server scope.
	const filteredEntries = useMemo(() => {
		const predicate = makeEntryPredicate(filters, dateFilter);
		return entries.filter(predicate);
	}, [dateFilter, entries, filters]);

	const sortBy = sorting[0]?.id ?? "date";
	const grouping = useMemo(() => [sortBy], [sortBy]);
	const tableColumnOrder = useMemo(
		() => [
			"_select",
			...DEFAULT_COLUMN_ORDER,
			"client",
			"task",
			"activity",
			"matterName",
			"billable",
			"unitsSeconds",
		],
		[],
	);
	const columnFilters = useMemo<ColumnFiltersState>(() => [], []);
	const tableColumns = useMemo(
		() => getEntriesColumns(timekeepers),
		[timekeepers],
	);

	const table = useReactTable({
		data: filteredEntries,
		columns: tableColumns,
		state: {
			sorting,
			grouping,
			expanded,
			columnVisibility,
			columnOrder: tableColumnOrder,
			rowSelection,
			columnFilters,
			columnSizing,
		},
		onSortingChange: handleSortingChange,
		onExpandedChange: setExpanded,
		onRowSelectionChange: setRowSelection,
		onColumnSizingChange: setColumnSizing,
		enableRowSelection: false,
		enableGrouping: true,
		autoResetExpanded: false,
		// This virtualized table has no pages. Pagination resets can feed a render
		// loop when query-derived filter/data references change while loading.
		autoResetPageIndex: false,
		enableColumnResizing: true,
		groupedColumnMode: false,
		columnResizeMode: "onEnd",
		defaultColumn: { minSize: 40, maxSize: 800 },
		getRowId: (row) => row.id,
		getCoreRowModel: getCoreRowModel(),
		getGroupedRowModel: getGroupedRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		meta: {} satisfies EntriesViewMeta,
	});

	const orderedEntries = table
		.getRowModel()
		.rows.filter((row: Row<Entry>) => !row.getIsGrouped())
		.map((row: Row<Entry>) => row.original);

	// Handle row click - canonical v2 entries already carry admin review data.
	const handleRowClick = (entry: Entry) => {
		setSelectedEntry(entry);
		setDialogOpen(true);
	};

	// Navigation: compute current index and bounds based on SORTED entries (not raw transactions)
	// This ensures navigation respects the current sort/filter applied by the user
	const currentIndex = useMemo(() => {
		if (!selectedEntry || !orderedEntries.length) return -1;
		return orderedEntries.findIndex((e) => e.id === selectedEntry.id);
	}, [selectedEntry, orderedEntries]);

	const hasPrevious = currentIndex > 0;
	const hasNext =
		currentIndex < orderedEntries.length - 1 && currentIndex !== -1;

	// Handle navigation between entries - use sorted/filtered entries array for correct order
	const handleNavigate = useCallback(
		(direction: "previous" | "next") => {
			const newIndex =
				direction === "previous" ? currentIndex - 1 : currentIndex + 1;
			if (newIndex >= 0 && newIndex < orderedEntries.length) {
				setSelectedEntry(orderedEntries[newIndex]);
			}
		},
		[currentIndex, orderedEntries],
	);

	// Handle saving feedback
	const handleSaveFeedback = useCallback(
		async (payload: FeedbackPayload) => {
			setIsSavingFeedback(true);
			try {
				const response = await makeApiRequest({
					endpoint: `/admin/transactions/${payload.entryId}/feedback`,
					method: "POST",
					body: {
						approved: payload.approved,
						needs_improvement: payload.needsImprovement,
						deducted: payload.deducted,
						attorney_feedback: payload.attorneyFeedback,
						hourglass_feedback: payload.hourglassFeedback,
					},
				});

				if (response.error) {
					// Entry no longer exists in transactions table (exported or deleted)
					if (response.error.toLowerCase().includes("not found")) {
						toast.error(
							"This entry has been exported or deleted and can no longer be updated",
							{ duration: 5000 },
						);
						throw new Error("Entry not found");
					}
					throw new Error(response.error);
				}

				const prediction = payload.needsImprovement
					? "needs_improvement"
					: payload.approved
						? "approved"
						: payload.deducted
							? "rejected"
							: null;
				const wireEntryId = formatEntryId(payload.entryId);
				const patchReview = (entry: Entry): Entry => ({
					...entry,
					adminReview: {
						originalDescription: entry.adminReview?.originalDescription ?? null,
						originalUnits: entry.adminReview?.originalUnits ?? null,
						originalMatterId: entry.adminReview?.originalMatterId ?? null,
						prediction,
						rejectionReason: payload.deducted,
						attorneyFeedback: payload.attorneyFeedback,
						hourglassFeedback: payload.hourglassFeedback,
					},
				});
				updateEntries((current) =>
					current.map((entry) =>
						entry.id === wireEntryId ? patchReview(entry) : entry,
					),
				);
				setSelectedEntry((current) =>
					current?.id === wireEntryId ? patchReview(current) : current,
				);

				logger.info("Feedback saved", {
					entryId: payload.entryId,
					approved: payload.approved,
					deducted: payload.deducted,
				});
				toast.success("Feedback saved");
			} catch (error) {
				const errorMsg = "Failed to save feedback";
				logger.error(errorMsg, {
					error: error instanceof Error ? error.message : String(error),
				});
				toast.error(errorMsg, { duration: 5000 });
				throw error; // Re-throw so dialog knows save failed
			} finally {
				setIsSavingFeedback(false);
			}
		},
		[makeApiRequest, updateEntries],
	);

	// Handle restore & export for deleted entries
	// isRestoring is managed here (not in the hook) so it covers the full operation including refetch
	const handleRestoreAndExport = useCallback(
		async (id: string): Promise<boolean> => {
			setIsRestoring(true);
			try {
				const success = await restoreAndExport(id);
				if (success) {
					await refetchEntries();
				}
				return success;
			} finally {
				setIsRestoring(false);
			}
		},
		[restoreAndExport, refetchEntries],
	);

	// Empty state message (matches attorney view pattern)
	const emptyMessage = useMemo(() => {
		if (filteredEntries.length > 0) return undefined;
		if (entries.length > 0) return "No entries match the current filter.";
		return "No entries in this range.";
	}, [filteredEntries.length, entries.length]);

	return (
		<div
			className={cn(
				"flex flex-col gap-4 p-6 bg-background h-full",
				styles.adminViewWrapper,
				styles.adminEntriesViewWrapper,
			)}
		>
			{/* Toolbar: filter (left), date range (center) */}
			<div className="grid grid-cols-3 items-center gap-3">
				<div className="flex items-center gap-3">
					<FilterMenu
						fieldOptions={fieldOptions}
						filters={filters}
						defaultFilters={defaultFilters}
						onToggleFieldValue={toggleFieldValue}
						onSelectAll={selectAll}
						onSelectNone={selectNone}
						onReset={reset}
						trigger={
							<Button
								variant="outline"
								size="icon"
								aria-label="Filter"
								className={cn(
									"relative",
									hasActiveFilters &&
										"border-ring text-ring hover:border-ring hover:text-ring",
								)}
							>
								<Filter />
								{hasActiveFilters && (
									<span className="absolute right-1 top-1 size-1.5 rounded-full bg-ring" />
								)}
							</Button>
						}
					/>
				</div>
				<div className="flex justify-center">
					<DatePicker
						className="w-fit"
						dateRange={selectedRange}
						onDateChange={setDate}
						onDateRangeChange={setDateRange}
						selectionMode="range"
						bottomSlot={DatePickerDefaultFooter}
					/>
				</div>
				<div />
			</div>

			{/* Entries table */}
			{(professionalsError || entriesError) && (
				<div
					role="alert"
					className="flex items-center gap-3 text-sm text-destructive"
				>
					<span>
						Could not load {professionalsError ? "professionals" : "entries"}.
						Previously loaded results may be out of date.
					</span>
					<Button
						variant="outline"
						size="sm"
						onClick={() =>
							void (professionalsError
								? refetchProfessionals()
								: refetchEntries())
						}
					>
						Retry
					</Button>
				</div>
			)}
			<div className="border border-border rounded-lg overflow-hidden flex-1 flex flex-col">
				{entriesLoading || profLoading ? (
					<div className="flex-1 flex items-center justify-center text-muted-foreground">
						Loading...
					</div>
				) : (
					<EntriesTable
						table={table}
						isLoading={entriesLoading}
						rowSelection={rowSelection}
						onRowClick={handleRowClick}
						exportedRowsInert={false}
						emptyMessage={emptyMessage}
						timekeepers={timekeepers}
					/>
				)}
			</div>

			{/* Detail Dialog */}
			<AdminEntryDetailDialog
				entry={selectedEntry}
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				onNavigate={handleNavigate}
				hasPrevious={hasPrevious}
				hasNext={hasNext}
				onSaveFeedback={handleSaveFeedback}
				isSaving={isSavingFeedback}
				onRestoreAndExport={handleRestoreAndExport}
				isRestoring={isRestoring}
			/>
		</div>
	);
}
